import { useCallback, useMemo } from "react";
import {
	UserPreferences,
	useCurrentUser,
	useUpdateMyPreferences,
} from "../../../networking/user.hooks";
import { applyColumnOrder, isDefaultOrder } from "./columnOrder";
import { ColumnOrder, ColumnOverrides, ColumnWidthOverrides, ListColumn } from "./types";

/** Everything a list page (and its toolbar picker) needs to drive column visibility. */
export interface ListColumns<T> {
	/** Columns available for this render (org-gated ones filtered out), in user order. */
	allColumns: ListColumn<T>[];
	/** Currently visible columns, in user order. */
	visibleColumns: ListColumn<T>[];
	/** Whether a column is currently visible. */
	isVisible: (key: string) => boolean;
	/** Toggle a column's visibility (no-op for mandatory / pinned / unknown columns). */
	toggle: (key: string) => void;
	/** Persist new relative width weights for one or more columns (merged). */
	setColumnWidths: (widths: ColumnWidthOverrides) => void;
	/** Drop a column's width override, reverting it to its coded default weight. */
	resetColumnWidth: (key: string) => void;
	/** Persist a new full order (called on drop). The pinned column is re-anchored to index 0. */
	reorder: (orderedKeys: string[]) => void;
	/** Clear the order, visibility and width overrides for this list in one update. */
	resetToDefault: () => void;
	/** Whether the user has any saved override for this list (visibility, width or order). */
	hasOverrides: boolean;
}

/** Locked-on columns cannot be hidden: explicitly mandatory, or the pinned anchor. */
function isLocked<T>(col: ListColumn<T>): boolean {
	return !!col.mandatory || !!col.pinned;
}

/** A column's visibility when the user has no override (locked ⇒ always on). */
function defaultVisible<T>(col: ListColumn<T>): boolean {
	return isLocked(col) || col.defaultVisible !== false;
}

/**
 * Column state for a virtualized list page — the column-picker analogue of
 * {@link useListFilters}. Generic over the row type and parameterized by the
 * preferences keys the overrides live under, so consultants, customers and jobs
 * share one mechanism.
 *
 * - Three override sets are persisted: visibility (`columnsKey`), relative width
 *   weights (`widthsKey`) and left-to-right order (`orderKey`). All of them store
 *   only the user's deviations from the coded defaults, so a column added in
 *   future code follows its default until the user touches it, and reverting to
 *   the default drops the override (an empty map / array reads as "no override").
 * - Availability (e.g. the org-gated tags column) is applied before defaults, so
 *   an unavailable column is never shown nor offered in the picker.
 * - The saved order is merged on read by {@link applyColumnOrder}, before
 *   visibility, so `allColumns` and `visibleColumns` both come back in user order
 *   and the pages need no render changes.
 * - A persisted width override replaces the column's `weight`; the page renders
 *   widths as `weight / Σ visible weights`, so overrides stay compatible with
 *   show/hide (the table always reflows to fill).
 */
export function useListColumns<T>(
	columnsKey: keyof UserPreferences,
	columns: ListColumn<T>[],
	widthsKey?: keyof UserPreferences,
	orderKey?: keyof UserPreferences
): ListColumns<T> {
	const { data: currentUser } = useCurrentUser();
	const { mutate: updatePreferences } = useUpdateMyPreferences();

	const preferences = currentUser?.profile?.preferences;
	const overrides = (preferences?.[columnsKey] ?? {}) as ColumnOverrides;
	const widthOverrides = (widthsKey ? preferences?.[widthsKey] ?? {} : {}) as ColumnWidthOverrides;
	const savedOrder = (orderKey ? preferences?.[orderKey] ?? [] : []) as ColumnOrder;

	// Apply persisted width overrides onto each column's weight up-front, so every
	// consumer (rendering + the resize handles) sees the effective weight. Kept in
	// coded order — it is the baseline the saved order is merged onto.
	const codedColumns = useMemo(
		() =>
			columns
				.filter((c) => c.available !== false)
				.map((c) => (widthOverrides[c.key] != null ? { ...c, weight: widthOverrides[c.key] } : c)),
		[columns, widthOverrides]
	);

	const allColumns = useMemo(
		() => applyColumnOrder(codedColumns, savedOrder),
		[codedColumns, savedOrder]
	);

	const isVisible = useCallback(
		(key: string): boolean => {
			const col = allColumns.find((c) => c.key === key);
			if (!col) return false;
			if (isLocked(col)) return true;
			const override = overrides[key];
			return override === undefined ? defaultVisible(col) : override;
		},
		[allColumns, overrides]
	);

	const visibleColumns = useMemo(
		() => allColumns.filter((c) => isVisible(c.key)),
		[allColumns, isVisible]
	);

	const toggle = useCallback(
		(key: string) => {
			const col = allColumns.find((c) => c.key === key);
			if (!col || isLocked(col)) return;

			const currentlyVisible = overrides[key] === undefined ? defaultVisible(col) : overrides[key];
			const nextVisible = !currentlyVisible;

			// Keep the map minimal: store only a real deviation from the default; if
			// the toggle lands back on the default, drop the override entirely.
			const next: ColumnOverrides = { ...overrides };
			if (nextVisible === defaultVisible(col)) delete next[key];
			else next[key] = nextVisible;

			updatePreferences({ [columnsKey]: next } as Partial<UserPreferences>);
		},
		[allColumns, overrides, updatePreferences, columnsKey]
	);

	const setColumnWidths = useCallback(
		(widths: ColumnWidthOverrides) => {
			if (!widthsKey) return;
			const next: ColumnWidthOverrides = { ...widthOverrides, ...widths };
			updatePreferences({ [widthsKey]: next } as Partial<UserPreferences>);
		},
		[widthsKey, widthOverrides, updatePreferences]
	);

	const resetColumnWidth = useCallback(
		(key: string) => {
			if (!widthsKey || widthOverrides[key] == null) return;
			const next: ColumnWidthOverrides = { ...widthOverrides };
			delete next[key];
			updatePreferences({ [widthsKey]: next } as Partial<UserPreferences>);
		},
		[widthsKey, widthOverrides, updatePreferences]
	);

	const reorder = useCallback(
		(orderedKeys: string[]) => {
			if (!orderKey) return;
			// Normalise what the picker hands us: unknown keys dropped, columns it did
			// not mention re-inserted next to their coded neighbour, pinned forced first.
			const next = applyColumnOrder(codedColumns, orderedKeys).map((c) => c.key);
			// Back at the coded order ⇒ no deviation left to store. `PUT /me/preferences`
			// shallow-merges the posted top-level keys into the stored preferences, so a
			// key is cleared by writing an empty value, which reads back as "no override".
			const value = isDefaultOrder(codedColumns, next) ? [] : next;
			updatePreferences({ [orderKey]: value } as Partial<UserPreferences>);
		},
		[orderKey, codedColumns, updatePreferences]
	);

	const resetToDefault = useCallback(() => {
		// All three keys in a single update. Empty values rather than `undefined`:
		// the endpoint merges the posted object into the stored preferences, so an
		// omitted key would simply keep its current value.
		const next: UserPreferences = { [columnsKey]: {} };
		if (widthsKey) next[widthsKey] = {};
		if (orderKey) next[orderKey] = [];
		updatePreferences(next);
	}, [columnsKey, widthsKey, orderKey, updatePreferences]);

	const hasOverrides =
		Object.keys(overrides).length > 0 ||
		Object.keys(widthOverrides).length > 0 ||
		(savedOrder.length > 0 && !isDefaultOrder(codedColumns, savedOrder));

	return {
		allColumns,
		visibleColumns,
		isVisible,
		toggle,
		setColumnWidths,
		resetColumnWidth,
		reorder,
		resetToDefault,
		hasOverrides,
	};
}
